/**
 * The signed-in user's settings, loaded from /api/user-settings.
 *
 * A switch flips the moment it is clicked and saves in the background. The save sends
 * only that one channel, so two quick clicks on different switches cannot overwrite each
 * other; a failed save puts the switch back and says so.
 */

import { useCallback, useEffect, useState } from 'react';
import { httpFetch } from '@/lib/httpClient';
import { getAuthToken } from '@/utils/authToken';
import { reportError } from '@/observability/clientLogger';
import {
  channelsFor,
  type NotificationChannel,
  type NotificationSettings,
  type UserSettings,
} from '@/settings/notificationSettings';

type LoadState = 'loading' | 'ready' | 'failed';

function headers(tenant: string | null | undefined): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    // Bearer on localhost, where the credential lives in localStorage; deployed, the
    // HttpOnly cookie authenticates and this is absent.
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenant ? { 'x-node': tenant } : {}),
  };
}

async function readSettings(response: Response): Promise<UserSettings> {
  const body = (await response.json().catch(() => null)) as { success?: boolean; settings?: UserSettings } | null;
  if (!response.ok || !body?.success || !body.settings) {
    throw new Error(`/api/user-settings returned ${response.status}`);
  }
  return body.settings;
}

export function useUserSettings(tenant: string | null | undefined) {
  const [notifications, setNotifications] = useState<NotificationSettings | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    // The session resolves the tenant first; asking before then would 401.
    if (!tenant) return;
    let cancelled = false;
    httpFetch('/api/user-settings', { headers: headers(tenant) })
      .then(readSettings)
      .then((settings) => {
        if (cancelled) return;
        setNotifications(settings.notifications);
        setState('ready');
      })
      .catch((error: unknown) => {
        reportError(error, { event_detail: 'user_settings_load_failed', app: 'shell-app' });
        if (!cancelled) setState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  const setChannel = useCallback(
    async (category: string, channel: NotificationChannel, enabled: boolean) => {
      setSaveError(null);
      // channelsFor fills the defaults for a category with nothing stored yet.
      setNotifications((prev) =>
        prev ? { ...prev, [category]: { ...channelsFor(prev, category), [channel]: enabled } } : prev,
      );

      try {
        const response = await httpFetch('/api/user-settings', {
          method: 'PUT',
          headers: headers(tenant),
          body: JSON.stringify({ notifications: { [category]: { [channel]: enabled } } }),
        });
        await readSettings(response);
      } catch (error) {
        reportError(error, { event_detail: 'user_settings_save_failed', app: 'shell-app', category, channel });
        // Only this switch goes back: a later click on another switch may already have
        // saved, and replacing the whole object would undo it.
        setNotifications((prev) =>
          prev ? { ...prev, [category]: { ...channelsFor(prev, category), [channel]: !enabled } } : prev,
        );
        setSaveError('Your change was not saved. Check your connection and try again.');
      }
    },
    [tenant],
  );

  return { notifications, state, saveError, setChannel };
}
